import { forwardRef, useRef } from 'react'
import ReactDatePicker from 'react-datepicker'
import './web/react-datepicker.css'
import { TextInput as PaperTextInput } from 'react-native-paper'

export interface DateTimePickerProps {
    value: Date | null
    onChange: (date: Date | null) => void
    mode?: 'date' | 'time' | 'datetime'
    minimumDate?: Date
    maximumDate?: Date
    minuteInterval?: number
    disabled?: boolean
    is24Hour?: boolean
    placeholder?: string
    filterDate?: (date: Date) => boolean
    excludeDates?: Date[]
    label?: string
    error?: boolean
}

interface CustomInputProps {
    value?: string
    onClick?: () => void
    placeholder?: string
    disabled?: boolean
    label?: string
    error?: boolean
    iconRef?: React.RefObject<Element | null>
}

const CustomInput = forwardRef<never, CustomInputProps>(
    ({ value, onClick, placeholder, disabled, label, error, iconRef }, _ref) => {
        return (
            <PaperTextInput
                value={value}
                onPress={onClick}
                placeholder={placeholder}
                disabled={disabled}
                label={label}
                mode="outlined"
                error={error}
                editable={false}
                right={
                    <PaperTextInput.Icon
                        icon="calendar"
                        onPress={onClick}
                        ref={iconRef as unknown as React.Ref<never>}
                    />
                }
                contentStyle={{ marginRight: 20 }}
                style={{ zIndex: 2 }}
            />
        )
    }
)

CustomInput.displayName = 'CustomInput'

function ensurePortalRoot() {
    let el = document.getElementById('datepicker-portal')
    if (!el) {
        el = document.createElement('div')
        el.id = 'datepicker-portal'
        el.style.position = 'fixed'
        el.style.top = '0'
        el.style.left = '0'
        el.style.zIndex = '99999'
        document.body.appendChild(el)
    }
    return el
}
if (typeof document !== 'undefined') {
    ensurePortalRoot()
}

export default function UnifiedDateTimePicker({
    value,
    onChange,
    mode = 'date',
    minimumDate,
    maximumDate,
    minuteInterval = 5,
    disabled = false,
    is24Hour = false,
    placeholder,
    filterDate,
    excludeDates,
    label,
    error,
}: DateTimePickerProps) {
    const iconRef = useRef<Element>(null)

    const getDateFormat = () => {
        if (mode === 'datetime') {
            return is24Hour ? 'yyyy-MM-dd HH:mm' : 'MM/dd/yyyy h:mm aa'
        }
        if (mode === 'time') {
            return is24Hour ? 'HH:mm' : 'h:mm aa'
        }
        return 'MM/dd/yyyy'
    }

    const input = (
        <CustomInput placeholder={placeholder} label={label} error={error} iconRef={iconRef} />
    )

    return (
        <ReactDatePicker
            selected={value}
            onChange={onChange}
            showTimeSelect={mode === 'time' || mode === 'datetime'}
            showTimeSelectOnly={mode === 'time'}
            timeIntervals={minuteInterval}
            dateFormat={getDateFormat()}
            minDate={minimumDate}
            maxDate={maximumDate}
            filterDate={filterDate}
            excludeDates={excludeDates}
            disabled={disabled}
            placeholderText={placeholder}
            popperPlacement="bottom-end"
            portalId="datepicker-portal"
            popperProps={{
                strategy: 'fixed',
                elements: {
                    reference: iconRef.current,
                },
            }}
            customInput={input}
        />
    )
}

export { UnifiedDateTimePicker as DateTimePicker }
